import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import { ui } from "../../../shared/styles/uiSystem"

function EmptyState({ loading, text }) {
  return (
    <div style={{ color: "#64748b", fontSize: 13 }}>
      {loading ? "Cargando..." : text}
    </div>
  )
}

function CollapseButton({ collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...ui.secondaryButton,
        minWidth: 42,
        minHeight: 36
      }}
    >
      {collapsed ? "+" : "−"}
    </button>
  )
}

function Tag({ children, tone = "neutral" }) {
  const tones = {
    neutral: {
      background: "#f8fafc",
      border: "#e2e8f0",
      color: "#334155"
    },
    danger: {
      background: "#fef2f2",
      border: "#fecaca",
      color: "#991b1b"
    },
    warning: {
      background: "#fffbeb",
      border: "#fde68a",
      color: "#92400e"
    },
    info: {
      background: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8"
    }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 24,
        padding: "0 8px",
        borderRadius: 999,
        background: visual.background,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.2
      }}
    >
      {children}
    </span>
  )
}

function SummaryMetric({ label, value, helper }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "grid",
        gap: 4,
        alignContent: "start"
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 22,
          lineHeight: 1.05,
          fontWeight: 900,
          color: "#0f172a"
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            lineHeight: 1.35
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  )
}

function AlertCard({ alert, onOpenAlert }) {
  const visual = getAlertVisual(alert.level)

  return (
    <button
      onClick={() => onOpenAlert(alert)}
      style={{
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        border: `1px solid ${visual.border}`,
        background: visual.background,
        borderRadius: 16,
        padding: 14,
        display: "grid",
        gap: 10,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div style={{ display: "grid", gap: 4, minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 800,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: 0.3
            }}
          >
            Alerta detectada
          </div>

          <div
            style={{
              fontWeight: 900,
              fontSize: 16,
              lineHeight: 1.25,
              color: visual.color,
              wordBreak: "break-word"
            }}
          >
            {alert.title}
          </div>

          <div
            style={{
              fontSize: 12.5,
              color: "#475569",
              fontWeight: 700
            }}
          >
            {alert.clinicName || "Sin clínica"}
          </div>
        </div>

        <Tag tone={visual.tagTone}>{visual.levelLabel}</Tag>
      </div>

      <div
        style={{
          color: "#334155",
          fontSize: 13,
          lineHeight: 1.45
        }}
      >
        {alert.description}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap"
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            fontWeight: 700
          }}
        >
          Atención inmediata
        </div>

        <div
          style={{
            fontSize: 12.5,
            color: "#0f172a",
            fontWeight: 900
          }}
        >
          Ver detalle →
        </div>
      </div>
    </button>
  )
}

export default function ExecutiveAlertsPanel({
  loading,
  alerts = [],
  collapsed,
  onToggle,
  onOpenAlert
}) {
  const rows = Array.isArray(alerts) ? alerts : []
  const highCount = rows.filter((item) => isLevel(item?.level, "HIGH")).length
  const mediumCount = rows.filter((item) => isLevel(item?.level, "MEDIUM")).length
  const lowCount = rows.filter((item) => isLevel(item?.level, "LOW")).length

  return (
    <SectionCard
      title="Alertas financieras"
      subtitle="Lo que requiere atención inmediata"
      actions={
        <CollapseButton
          collapsed={collapsed}
          onClick={onToggle}
        />
      }
    >
      {!collapsed && (
        <>
          {!rows.length ? (
            <EmptyState loading={loading} text="Sin alertas financieras relevantes." />
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 10
                }}
              >
                <SummaryMetric
                  label="Alertas activas"
                  value={formatCount(rows.length)}
                  helper="Incidencias visibles hoy"
                />
                <SummaryMetric
                  label="Alta prioridad"
                  value={formatCount(highCount)}
                  helper="Requieren atención primero"
                />
                <SummaryMetric
                  label="Prioridad media"
                  value={formatCount(mediumCount)}
                  helper="Seguimiento importante"
                />
                <SummaryMetric
                  label="Prioridad baja"
                  value={formatCount(lowCount)}
                  helper="Atención preventiva"
                />
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                {rows.map((alert, index) => (
                  <AlertCard
                    key={`${alert.code}-${alert.clinicId}-${index}`}
                    alert={alert}
                    onOpenAlert={onOpenAlert}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}

function getAlertVisual(level) {
  const raw = String(level || "").trim().toUpperCase()

  if (raw === "HIGH") {
    return {
      border: "#fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      tagTone: "danger",
      levelLabel: "Alta prioridad"
    }
  }

  if (raw === "MEDIUM") {
    return {
      border: "#fde68a",
      background: "#fffbeb",
      color: "#92400e",
      tagTone: "warning",
      levelLabel: "Prioridad media"
    }
  }

  return {
    border: "#bfdbfe",
    background: "#eff6ff",
    color: "#1d4ed8",
    tagTone: "info",
    levelLabel: "Prioridad baja"
  }
}

function isLevel(level, expected) {
  return String(level || "").trim().toUpperCase() === expected
}

function formatCount(value) {
  const parsed = Number(value)
  const safe = Number.isFinite(parsed) ? parsed : 0

  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(safe)
}